import React, { useEffect, useState } from "react";
import { Upload, FileText, Trash2, Download, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { API, apiErrorMessage } from "@/lib/api";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useLang } from "@/context/LanguageContext";

const DOC_TYPES = {
  fr: ["Passeport", "CV", "Diplôme", "Justificatif de domicile", "Photo d'identité", "Autre"],
  en: ["Passport", "CV", "Diploma", "Proof of address", "ID photo", "Other"],
};

const C = {
  fr: { title: "Mes documents", subtitle: "Téléversez vos documents (passeport, CV…) en toute sécurité.",
    type: "Type de document", choose: "Choisir un fichier", uploading: "Téléversement…",
    formats: "Formats acceptés : PDF, JPG, PNG, DOCX · Max 10 Mo", none: "Aucun document",
    noneText: "Vos documents téléversés apparaîtront ici.", uploadOk: "Document téléversé avec succès !",
    deleteOk: "Document supprimé.", deleteErr: "Erreur lors de la suppression.",
    myDocs: "Mes documents envoyés", agencyDocs: "Documents transmis par l'agence",
    agencyEmpty: "Aucun document reçu de l'agence pour le moment.", agencyBadge: "Agence" },
  en: { title: "My documents", subtitle: "Upload your documents (passport, CV…) securely.",
    type: "Document type", choose: "Choose a file", uploading: "Uploading…",
    formats: "Accepted formats: PDF, JPG, PNG, DOCX · Max 10 MB", none: "No document",
    noneText: "Your uploaded documents will appear here.", uploadOk: "Document uploaded successfully!",
    deleteOk: "Document deleted.", deleteErr: "Error while deleting.",
    myDocs: "My uploaded documents", agencyDocs: "Documents sent by the agency",
    agencyEmpty: "No document received from the agency yet.", agencyBadge: "Agency" },
};

export default function Documents() {
  const { lang } = useLang();
  const c = C[lang];
  const types = DOC_TYPES[lang];
  const [docs, setDocs] = useState([]);
  const [docType, setDocType] = useState(types[0]);
  const [uploading, setUploading] = useState(false);

  const load = () => api.get("/documents/me").then((r) => setDocs(r.data)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { setDocType(types[0]); }, [lang]);

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    const fd = new FormData();
    fd.append("file", file);
    fd.append("doc_type", docType);
    try {
      await api.post("/documents", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(c.uploadOk);
      load();
    } catch (err) {
      toast.error(apiErrorMessage(err.response?.data?.detail));
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const remove = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success(c.deleteOk); load(); }
    catch { toast.error(c.deleteErr); }
  };

  const download = (id) => {
    const token = localStorage.getItem("wte_token");
    window.open(`${API}/documents/${id}/download?auth=${token}`, "_blank");
  };

  return (
    <div data-testid="documents-page">
      <h1 className="font-heading text-3xl sm:text-4xl text-navy">{c.title}</h1>
      <p className="text-muted-foreground mt-2">{c.subtitle}</p>

      <div className="bg-white border border-border p-6 mt-8">
        <div className="flex flex-col sm:flex-row gap-4 sm:items-end">
          <div className="flex-1">
            <label className="text-sm text-navy font-medium">{c.type}</label>
            <Select value={docType} onValueChange={setDocType}>
              <SelectTrigger data-testid="doc-type-select" className="mt-2 rounded-none"><SelectValue /></SelectTrigger>
              <SelectContent>
                {types.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <label data-testid="upload-label" className={`flex items-center justify-center gap-2 bg-gold hover:bg-gold-hover text-white px-6 py-3 font-medium cursor-pointer transition-colors ${uploading ? "opacity-60 pointer-events-none" : ""}`}>
            {uploading ? <><Loader2 size={16} className="animate-spin" /> {c.uploading}</> : <><Upload size={16} /> {c.choose}</>}
            <input type="file" data-testid="doc-file-input" className="hidden" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" onChange={onUpload} />
          </label>
        </div>
        <p className="text-xs text-muted-foreground mt-3">{c.formats}</p>
      </div>

      <div className="mt-8">
        <h2 className="font-heading text-2xl text-navy flex items-center gap-2"><Download size={20} className="text-gold" /> {c.agencyDocs}</h2>
        {(() => {
          const agencyDocs = docs.filter((d) => d.uploaded_by === "advisor");
          return agencyDocs.length === 0 ? (
            <div className="bg-white border border-border p-6 text-center mt-3 text-muted-foreground text-sm">{c.agencyEmpty}</div>
          ) : (
            <div className="bg-white border border-border divide-y divide-border mt-3">
              {agencyDocs.map((d) => (
                <div key={d.id} data-testid={`agency-doc-${d.id}`} className="flex items-center justify-between p-4 gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 bg-gold text-white flex items-center justify-center shrink-0"><FileText size={18} /></div>
                    <div className="min-w-0">
                      <p className="text-navy font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{d.doc_type} · {(d.size / 1024).toFixed(0)} Ko</p>
                    </div>
                    <span className="text-[10px] uppercase tracking-widest bg-gold/10 text-gold px-2 py-1 shrink-0">{c.agencyBadge}</span>
                  </div>
                  <button data-testid={`agency-doc-download-${d.id}`} onClick={() => download(d.id)} className="p-2 text-navy hover:text-gold transition-colors shrink-0"><Download size={18} /></button>
                </div>
              ))}
            </div>
          );
        })()}
      </div>

      <div className="mt-10">
        <h2 className="font-heading text-2xl text-navy flex items-center gap-2"><Upload size={20} className="text-gold" /> {c.myDocs}</h2>
        {(() => {
          const myDocs = docs.filter((d) => d.uploaded_by !== "advisor");
          return myDocs.length === 0 ? (
            <div className="bg-white border border-border p-10 text-center mt-3">
              <FileText size={40} className="mx-auto text-gold" />
              <p className="text-navy font-medium mt-4">{c.none}</p>
              <p className="text-muted-foreground text-sm mt-1">{c.noneText}</p>
            </div>
          ) : (
            <div className="bg-white border border-border divide-y divide-border mt-3">
              {myDocs.map((d) => (
                <div key={d.id} data-testid={`doc-${d.id}`} className="flex items-center justify-between p-4 gap-4">
                  <div className="flex items-center gap-4 min-w-0">
                    <div className="h-10 w-10 bg-navy text-gold flex items-center justify-center shrink-0"><FileText size={18} /></div>
                    <div className="min-w-0">
                      <p className="text-navy font-medium truncate">{d.original_filename}</p>
                      <p className="text-xs text-muted-foreground">{d.doc_type} · {(d.size / 1024).toFixed(0)} Ko</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <button data-testid={`doc-download-${d.id}`} onClick={() => download(d.id)} className="p-2 text-navy hover:text-gold transition-colors"><Download size={18} /></button>
                    <button data-testid={`doc-delete-${d.id}`} onClick={() => remove(d.id)} className="p-2 text-navy hover:text-red-600 transition-colors"><Trash2 size={18} /></button>
                  </div>
                </div>
              ))}
            </div>
          );
        })()}
      </div>
    </div>
  );
}
